import { useState } from 'react';
import { Button } from '../../../components/ui/button';
import { PhaseSubTabs } from '../parts/PhaseSubTabs';
import type { ExpectedField, ImportBatchRow } from '@discovr/types';
import { RosterConflictCard } from './RosterConflictCard';
import { conflictRows, successfulRows, rosterUndecidedRows, isRosterReviewComplete } from './importWizardHelpers';
import { WizardFooter } from './ImportWizardFooterSlot';
import { RowNumberBadge } from './ImportRowBadge';

interface ImportRosterConflictsStepProps {
  rows: ImportBatchRow[];
  expectedFields: ExpectedField[];
  busy: boolean;
  onResolve: (
    rowNumber: number,
    action: 'keep_existing' | 'overwrite' | 'skip',
    keepFields?: string[],
  ) => Promise<void>;
  onBack: () => Promise<void>;
  onContinue: () => void;
}

type RosterConflictsTab = 'all' | 'new' | 'inRoster' | 'undecided';

/** A compact, gap-less list of new (non-conflicting) rows — used by both the
 *  "New" tab and the "All" tab's new-creators block. */
function NewRowsList({ rows }: { rows: ImportBatchRow[] }) {
  if (rows.length === 0) {
    return <p className="text-body-sm-regular text-muted-foreground">No new creators in this sheet.</p>;
  }
  return (
    <div className="divide-y divide-border overflow-hidden rounded-lg border border-border">
      {rows.map((row) => {
        const username = row.values.username || '—';
        return (
          <div key={row.rowNumber} className="flex items-center gap-3 bg-card px-3 py-2 transition-colors hover:bg-muted/40">
            <RowNumberBadge n={row.rowNumber} />
            <span className="min-w-0 flex-1 truncate text-body-sm-medium text-foreground">{username}</span>
            <span className="shrink-0 text-caption-regular text-muted-foreground">Will be added</span>
          </div>
        );
      })}
    </div>
  );
}

export function ImportRosterConflictsStep({ rows, expectedFields, busy, onResolve, onBack, onContinue }: ImportRosterConflictsStepProps) {
  const conflicts = conflictRows(rows);
  const successful = successfulRows(rows);
  const undecided = rosterUndecidedRows(rows);
  const [tab, setTab] = useState<RosterConflictsTab>(conflicts.length > 0 ? 'inRoster' : 'new');
  const complete = isRosterReviewComplete(rows);

  return (
    <div className="flex flex-col gap-4">
      <PhaseSubTabs
        variant="underline"
        active={tab}
        tabs={[
          { key: 'all', label: 'All', count: rows.length },
          { key: 'new', label: 'New', count: successful.length },
          { key: 'inRoster', label: 'In roster', count: conflicts.length },
          { key: 'undecided', label: 'Undecided', count: undecided.length },
        ]}
        onChange={setTab}
      />

      {tab === 'all' && (
        <div className="flex flex-col gap-4 pt-3">
          {conflicts.length > 0 && (
            <div className="flex flex-col gap-2">
              <h4 className="text-caption-medium text-muted-foreground">In roster</h4>
              <div className="flex flex-col gap-3">
                {conflicts.map((row) => (
                  <RosterConflictCard key={row.rowNumber} row={row} expectedFields={expectedFields} busy={busy} onResolve={onResolve} />
                ))}
              </div>
            </div>
          )}
          {successful.length > 0 && (
            <div className="flex flex-col gap-2">
              <h4 className="text-caption-medium text-muted-foreground">New</h4>
              <NewRowsList rows={successful} />
            </div>
          )}
        </div>
      )}

      {tab === 'new' && (
        <div className="pt-3">
          <NewRowsList rows={successful} />
        </div>
      )}

      {tab === 'inRoster' && (
        <div className="flex flex-col gap-2 pt-3">
          {conflicts.length === 0 && (
            <p className="text-body-sm-regular text-muted-foreground">No roster conflicts.</p>
          )}
          {conflicts.map((row) => (
            <RosterConflictCard key={row.rowNumber} row={row} expectedFields={expectedFields} busy={busy} onResolve={onResolve} />
          ))}
        </div>
      )}

      {tab === 'undecided' && (
        <div className="flex flex-col gap-2 pt-3">
          {undecided.length === 0 && (
            <p className="text-body-sm-regular text-muted-foreground">Nothing left to decide.</p>
          )}
          {undecided.map((row) => (
            <RosterConflictCard key={row.rowNumber} row={row} expectedFields={expectedFields} busy={busy} onResolve={onResolve} />
          ))}
        </div>
      )}

      <WizardFooter>
        <div className="flex w-full items-center justify-between">
          <Button type="button" variant="outline" size="lg" disabled={busy} onClick={() => { onBack().catch(() => {}); }}>
            Back
          </Button>
          <Button
            type="button"
            variant="default"
            size="lg"
            loading={busy}
            disabled={!complete}
            onClick={onContinue}
          >
            Continue
          </Button>
        </div>
      </WizardFooter>
    </div>
  );
}
